import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Truck, Users, Plus, Search, Filter, Calendar, Wrench, Fuel, Gauge,
  MapPin, Clock, CheckCircle2, AlertTriangle, XCircle, Edit, Eye,
  FileText, Award, Shield, Phone, Mail, Navigation
} from 'lucide-react';

const FleetManagement = () => {
  const [activeTab, setActiveTab] = useState('vehicles'); // vehicles, drivers
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data for vehicles
  const vehicles = [
    {
      id: 'VH-001',
      type: 'Heavy Truck',
      make: 'Mercedes-Benz',
      model: 'Actros 1845',
      year: 2022,
      licensePlate: 'ABC-1234',
      capacity: { weight: 15000, volume: 45.0 }, // kg, m³
      fuelType: 'Diesel',
      status: 'in_transit',
      currentLocation: 'Riyadh → Jeddah Highway',
      driver: 'Ahmed Al-Rashid',
      odometer: 125000,
      lastMaintenance: '2024-01-15',
      nextMaintenance: '2024-02-15',
      fuelLevel: 75,
      maintenanceLogs: [
        { date: '2024-01-15', type: 'Regular Service', cost: 2500, mileage: 123000 },
        { date: '2023-12-10', type: 'Tire Replacement', cost: 3200, mileage: 118000 },
        { date: '2023-11-05', type: 'Oil Change', cost: 800, mileage: 115000 }
      ],
      insuranceExpiry: '2024-12-31',
      registrationExpiry: '2024-06-30'
    },
    {
      id: 'VH-002',
      type: 'Medium Van',
      make: 'Ford',
      model: 'Transit 350',
      year: 2023,
      licensePlate: 'XYZ-5678',
      capacity: { weight: 2000, volume: 12.0 },
      fuelType: 'Diesel',
      status: 'available',
      currentLocation: 'Riyadh Hub',
      driver: null,
      odometer: 45000,
      lastMaintenance: '2024-01-20',
      nextMaintenance: '2024-02-20',
      fuelLevel: 90,
      maintenanceLogs: [
        { date: '2024-01-20', type: 'Regular Service', cost: 1500, mileage: 44000 },
        { date: '2023-12-15', type: 'Brake Inspection', cost: 600, mileage: 40000 }
      ],
      insuranceExpiry: '2024-11-30',
      registrationExpiry: '2024-05-31'
    },
    {
      id: 'VH-003',
      type: 'Heavy Truck',
      make: 'Volvo',
      model: 'FH16 540',
      year: 2021,
      licensePlate: 'DEF-9012',
      capacity: { weight: 20000, volume: 50.0 },
      fuelType: 'Diesel',
      status: 'maintenance',
      currentLocation: 'Service Center - Riyadh',
      driver: null,
      odometer: 180000,
      lastMaintenance: '2024-01-25',
      nextMaintenance: '2024-02-25',
      fuelLevel: 20,
      maintenanceLogs: [
        { date: '2024-01-25', type: 'Engine Overhaul', cost: 15000, mileage: 178000 },
        { date: '2023-12-20', type: 'Transmission Repair', cost: 8000, mileage: 175000 }
      ],
      insuranceExpiry: '2024-10-31',
      registrationExpiry: '2024-04-30'
    },
    {
      id: 'VH-004',
      type: 'Light Truck',
      make: 'Isuzu',
      model: 'NPR 75',
      year: 2023,
      licensePlate: 'GHI-3456',
      capacity: { weight: 3500, volume: 18.0 },
      fuelType: 'Diesel',
      status: 'available',
      currentLocation: 'Dammam Hub',
      driver: null,
      odometer: 32000,
      lastMaintenance: '2024-01-10',
      nextMaintenance: '2024-02-10',
      fuelLevel: 85,
      maintenanceLogs: [
        { date: '2024-01-10', type: 'Regular Service', cost: 1200, mileage: 30000 }
      ],
      insuranceExpiry: '2024-12-31',
      registrationExpiry: '2024-06-30'
    }
  ];

  // Mock data for drivers
  const drivers = [
    {
      id: 'DR-001',
      name: 'Ahmed Al-Rashid',
      licenseNumber: 'DL-123456',
      licenseType: 'Heavy Vehicle',
      licenseExpiry: '2025-06-30',
      phone: '+966501234567',
      email: 'ahmed.rashid@company.com',
      status: 'on_duty',
      currentVehicle: 'VH-001',
      currentLocation: 'Riyadh → Jeddah Highway',
      certifications: [
        { name: 'Hazmat Certified', expiry: '2025-12-31' },
        { name: 'Defensive Driving', expiry: '2024-12-31' }
      ],
      performance: {
        totalDeliveries: 245,
        onTimeRate: 96.5,
        safetyScore: 98.2,
        averageRating: 4.8
      },
      availability: {
        monday: true, tuesday: true, wednesday: true, thursday: true,
        friday: false, saturday: true, sunday: true
      },
      assignedTasks: [
        { id: 'LOG-2024-003', from: 'Riyadh', to: 'Jeddah', status: 'in-progress' }
      ]
    },
    {
      id: 'DR-002',
      name: 'Mohammed Ali',
      licenseNumber: 'DL-234567',
      licenseType: 'Medium Vehicle',
      licenseExpiry: '2024-12-31',
      phone: '+966502345678',
      email: 'mohammed.ali@company.com',
      status: 'available',
      currentVehicle: null,
      currentLocation: 'Riyadh Hub',
      certifications: [
        { name: 'Defensive Driving', expiry: '2024-12-31' }
      ],
      performance: {
        totalDeliveries: 189,
        onTimeRate: 94.2,
        safetyScore: 96.5,
        averageRating: 4.6
      },
      availability: {
        monday: true, tuesday: true, wednesday: true, thursday: true,
        friday: true, saturday: true, sunday: false
      },
      assignedTasks: []
    },
    {
      id: 'DR-003',
      name: 'Omar Hassan',
      licenseNumber: 'DL-345678',
      licenseType: 'Heavy Vehicle',
      licenseExpiry: '2025-03-31',
      phone: '+966503456789',
      email: 'omar.hassan@company.com',
      status: 'off_duty',
      currentVehicle: null,
      currentLocation: 'Home',
      certifications: [
        { name: 'Hazmat Certified', expiry: '2025-06-30' },
        { name: 'Defensive Driving', expiry: '2024-12-31' },
        { name: 'Crane Operation', expiry: '2025-09-30' }
      ],
      performance: {
        totalDeliveries: 312,
        onTimeRate: 97.8,
        safetyScore: 99.1,
        averageRating: 4.9
      },
      availability: {
        monday: true, tuesday: true, wednesday: true, thursday: true,
        friday: false, saturday: false, sunday: false
      },
      assignedTasks: []
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-700';
      case 'in_transit': return 'bg-blue-100 text-blue-700';
      case 'maintenance': return 'bg-orange-100 text-orange-700';
      case 'on_duty': return 'bg-blue-100 text-blue-700';
      case 'off_duty': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredVehicles = vehicles.filter(v =>
    v.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.licensePlate.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.make.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDrivers = drivers.filter(d =>
    d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.licenseNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Fleet & Driver Management</h2>
            <p className="text-sm text-gray-600 mt-1">Manage vehicles, drivers, certifications, and availability</p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <button
              onClick={() => alert('Add new vehicle/driver. This would open a form.')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Add New
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => setActiveTab('vehicles')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'vehicles'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Truck className="w-4 h-4 inline mr-2" />
            Vehicles ({vehicles.length})
          </button>
          <button
            onClick={() => setActiveTab('drivers')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'drivers'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Drivers ({drivers.length})
          </button>
        </div>
      </div>

      {/* Vehicles Tab */}
      {activeTab === 'vehicles' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredVehicles.map((vehicle) => (
            <motion.div
              key={vehicle.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Truck className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{vehicle.id}</h3>
                    <p className="text-sm text-gray-600">{vehicle.make} {vehicle.model} ({vehicle.year})</p>
                    <p className="text-xs text-gray-500 mt-1">License: {vehicle.licensePlate}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(vehicle.status)}`}>
                  {vehicle.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Capacity</p>
                  <p className="font-medium text-gray-900">{vehicle.capacity.weight.toLocaleString()} kg</p>
                  <p className="text-xs text-gray-600">{vehicle.capacity.volume} m³</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Fuel Level</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${vehicle.fuelLevel > 25 ? 'bg-green-500' : 'bg-red-500'}`}
                        style={{ width: `${vehicle.fuelLevel}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-900">{vehicle.fuelLevel}%</span>
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Odometer</p>
                  <p className="font-medium text-gray-900">{vehicle.odometer.toLocaleString()} km</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Next Maintenance</p>
                  <p className="font-medium text-gray-900">{vehicle.nextMaintenance}</p>
                </div>
              </div>

              {vehicle.driver && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-900">Driver: {vehicle.driver}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <span className="text-xs text-gray-600">{vehicle.currentLocation}</span>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedVehicle(vehicle.id === selectedVehicle ? null : vehicle.id)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  <Eye className="w-4 h-4 inline mr-2" />
                  View Details
                </button>
                <button
                  onClick={() => alert(`Editing vehicle ${vehicle.id}`)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </div>

              {/* Expanded Details */}
              {selectedVehicle === vehicle.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-4 pt-4 border-t border-gray-200 space-y-4"
                >
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Maintenance Logs</h4>
                    <div className="space-y-2">
                      {vehicle.maintenanceLogs.map((log, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{log.type}</p>
                              <p className="text-xs text-gray-600">{log.date} • {log.mileage.toLocaleString()} km</p>
                            </div>
                            <span className="font-medium text-gray-900">SAR {log.cost.toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Insurance Expiry</p>
                      <p className="font-medium text-gray-900">{vehicle.insuranceExpiry}</p>
                    </div>
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Registration Expiry</p>
                      <p className="font-medium text-gray-900">{vehicle.registrationExpiry}</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Drivers Tab */}
      {activeTab === 'drivers' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredDrivers.map((driver) => (
            <motion.div
              key={driver.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{driver.name}</h3>
                    <p className="text-sm text-gray-600">License: {driver.licenseNumber}</p>
                    <p className="text-xs text-gray-500 mt-1">{driver.licenseType}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(driver.status)}`}>
                  {driver.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Total Deliveries</p>
                  <p className="font-medium text-gray-900">{driver.performance.totalDeliveries}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">On-Time Rate</p>
                  <p className="font-medium text-gray-900">{driver.performance.onTimeRate}%</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Safety Score</p>
                  <p className="font-medium text-gray-900">{driver.performance.safetyScore}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Rating</p>
                  <p className="font-medium text-gray-900">{driver.performance.averageRating} / 5.0</p>
                </div>
              </div>

              {driver.currentVehicle && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-900">Vehicle: {driver.currentVehicle}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <span className="text-xs text-gray-600">{driver.currentLocation}</span>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedDriver(driver.id === selectedDriver ? null : driver.id)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  <Eye className="w-4 h-4 inline mr-2" />
                  View Details
                </button>
                <button
                  onClick={() => alert(`Contacting ${driver.name}`)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Phone className="w-4 h-4" />
                </button>
              </div>

              {/* Expanded Details */}
              {selectedDriver === driver.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-4 pt-4 border-t border-gray-200 space-y-4"
                >
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Certifications</h4>
                    <div className="space-y-2">
                      {driver.certifications.map((cert, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Award className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-gray-900">{cert.name}</span>
                          </div>
                          <span className="text-xs text-gray-600">Expires: {cert.expiry}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Contact Information</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span>{driver.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span>{driver.email}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">License Expiry</h4>
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="font-medium text-gray-900">{driver.licenseExpiry}</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FleetManagement;
